import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Button,
  Typography,
  Row,
  Col,
  Input,
  Modal,
  Form,
  Tag,
  Empty,
  Spin,
  Tooltip,
  Badge,
  Pagination,
} from "antd";
import {
  FaPlus,
  FaCheckCircle,
  FaStopwatch,
  FaSearch,
  FaFire,
  FaClock,
  FaCalendarDay,
  FaTasks,
  FaRegCircle,
  FaLock,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

/* ─── Cores do projeto ─── */
const gradientPrimary = "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)";
const successColor = "#10b981";
const primaryColor = "#1677ff";

/* ─── Live Clock ─────────────────────────────────────────────────── */
const LiveClock = () => {
  const [time, setTime] = useState(dayjs());
  useEffect(() => {
    const t = setInterval(() => setTime(dayjs()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span style={{ fontVariantNumeric: "tabular-nums", letterSpacing: 1 }}>
      {time.format("HH:mm:ss")}
    </span>
  );
};

/* ─── Running Timer ───────────────────────────────────────────────── */
const RunningTimer = ({ inicio }) => {
  const [elapsed, setElapsed] = useState("");
  useEffect(() => {
    const calc = () => {
      const diff = dayjs().diff(dayjs(inicio), "second");
      const h = Math.floor(diff / 3600).toString().padStart(2, "0");
      const m = Math.floor((diff % 3600) / 60).toString().padStart(2, "0");
      const s = (diff % 60).toString().padStart(2, "0");
      setElapsed(`${h}h:${m}min:${s}s`);
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [inicio]);
  return (
    <span
      style={{
        fontVariantNumeric: "tabular-nums",
        fontWeight: 700,
        color: primaryColor,
        fontSize: 13,
        letterSpacing: 0.5,
      }}
    >
      ⏱ {elapsed}
    </span>
  );
};

/* ─── Add Task Modal ─────────────────────────────────────────────── */
const AddTarefaModal = ({ open, onClose, onSuccess, userId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await Api.AddTarefaDiaria({
        id_user: userId,
        titulo: values.titulo,
        descricao: values.descricao,
      });
      toast.success("Tarefa iniciada!");
      form.resetFields();
      onSuccess();
      onClose();
    } catch (err) {
      if (err.errorFields) {
        toast.error("Preencha todos os campos.");
      } else {
        toast.error("Erro ao criar tarefa.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={520}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: gradientPrimary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaFire style={{ color: "#fff", fontSize: 16 }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#111" }}>
              Nova Tarefa Diária
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af", fontWeight: 400 }}>
              O cronômetro inicia automaticamente
            </div>
          </div>
        </div>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 8 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          onClick={handleSubmit}
          loading={loading}
          style={{
            borderRadius: 8,
            background: gradientPrimary,
            border: "none",
            color: "#fff",
            fontWeight: 600,
            boxShadow: "0 4px 12px rgba(22,119,255,0.3)",
          }}
        >
          Iniciar Tarefa
        </Button>,
      ]}
      styles={{ body: { padding: "24px 24px 0" } }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={<span style={{ fontWeight: 600, color: "#374151" }}>Título</span>}
          name="titulo"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <Input
            size="large"
            placeholder="O que você vai fazer agora?"
            style={{ borderRadius: 10 }}
          />
        </Form.Item>
        <Form.Item
          label={<span style={{ fontWeight: 600, color: "#374151" }}>Descrição</span>}
          name="descricao"
          rules={[{ required: true, message: "Campo obrigatório" }]}
        >
          <TextArea
            rows={3}
            placeholder="Detalhes da tarefa..."
            style={{ borderRadius: 10, resize: "none" }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/* ─── Task Card (redesenhado) ───────────────────────────────────── */
const TarefaCard = ({ tarefa, onClose }) => {
  const isAberto = tarefa.status === "ABERTO";

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${isAberto ? "rgba(22,119,255,0.2)" : "#e5e7eb"}`,
        borderRadius: 14,
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        transition: "all 0.2s ease",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        position: "relative",
        overflow: "hidden",
        cursor: "default",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)";
        e.currentTarget.style.transform = "none";
      }}
    >
      {/* Faixa lateral */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: isAberto ? gradientPrimary : `linear-gradient(180deg, ${successColor}, #059669)`,
        }}
      />

      {/* Ícone + conteúdo */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 0 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: isAberto ? "rgba(22,119,255,0.1)" : "rgba(16,185,129,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {isAberto ? (
            <FaRegCircle style={{ color: primaryColor, fontSize: 18 }} />
          ) : (
            <FaCheckCircle style={{ color: successColor, fontSize: 18 }} />
          )}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Text strong style={{ fontSize: 15, color: "#1f2937" }}>
              {tarefa.titulo}
            </Text>
            <Tag
              style={{
                borderRadius: 20,
                fontSize: 10,
                fontWeight: 700,
                padding: "1px 8px",
                border: "none",
                background: isAberto ? "#e6f0ff" : "#ecfdf5",
                color: isAberto ? primaryColor : successColor,
                letterSpacing: 0.5,
              }}
            >
              {isAberto ? "EM ANDAMENTO" : "CONCLUÍDA"}
            </Tag>
          </div>
          <Text style={{ fontSize: 13, color: "#6b7280", display: "block", marginBottom: 6 }}>
            {tarefa.descricao}
          </Text>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#9ca3af", display: "flex", alignItems: "center", gap: 4 }}>
              <FaClock style={{ fontSize: 10 }} />
              Início: {dayjs(tarefa.inicio).format("HH:mm")}
            </span>
            {isAberto ? (
              <RunningTimer inicio={tarefa.inicio} />
            ) : (
              <span style={{ fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
                <FaStopwatch style={{ fontSize: 10, color: successColor }} />
                Duração: <strong style={{ color: "#059669", marginLeft: 3 }}>{tarefa.duracao}</strong>
              </span>
            )}
            {!isAberto && tarefa.fim && (
              <span style={{ fontSize: 12, color: "#9ca3af", display: "flex", alignItems: "center", gap: 4 }}>
                <FaLock style={{ fontSize: 10 }} />
                Fim: {dayjs(tarefa.fim).format("HH:mm")}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Ação de concluir (apenas abertas) */}
      {isAberto && (
        <Tooltip title="Concluir tarefa">
          <Button
            onClick={() => onClose(tarefa)}
            style={{
              background: "linear-gradient(135deg, #10b981, #059669)",
              border: "none",
              color: "#fff",
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 12,
              height: 34,
              padding: "0 14px",
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
              boxShadow: "0 4px 10px rgba(16,185,129,0.3)",
            }}
          >
            <FaCheckCircle />
            Concluir
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

/* ─── Main Page ───────────────────────────────────────────────────── */
const TarefaDiaria = () => {
  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [closingTarefa, setClosingTarefa] = useState(null);

  // Paginação
  const pageSize = 5;
  const [openPage, setOpenPage] = useState(1);
  const [closedPage, setClosedPage] = useState(1);

  const userStored = JSON.parse(localStorage.getItem("user")) || {};
  const today = dayjs().format("dddd, DD [de] MMMM");

  const loadTarefas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.ListTarefaDiaria({ id_user: userStored.id });
      setTarefas(Array.isArray(response) ? response : []);
      // Reset pages quando recarregar
      setOpenPage(1);
      setClosedPage(1);
    } catch {
      toast.error("Erro ao carregar tarefas diárias.");
      setTarefas([]);
    } finally {
      setLoading(false);
    }
  }, [userStored.id]);

  useEffect(() => {
    document.title = "Tarefas Diárias";
    loadTarefas();
  }, [loadTarefas]);

  const handleCloseTarefa = async (tarefa) => {
    try {
      setClosingTarefa(tarefa.id);
      await Api.AlterTarefaDiaria({ id: tarefa.id });
      toast.success("Tarefa concluída!");
      loadTarefas();
    } catch {
      toast.error("Erro ao fechar tarefa.");
    } finally {
      setClosingTarefa(null);
    }
  };

  // Filtragem por busca
  const filtered = tarefas.filter((t) => {
    const q = search.toLowerCase();
    return (
      !q ||
      t.titulo?.toLowerCase().includes(q) ||
      t.descricao?.toLowerCase().includes(q)
    );
  });

  const abertas = filtered.filter((t) => t.status === "ABERTO");
  const fechadas = filtered.filter((t) => t.status === "FECHADO");

  // Paginação das abertas
  const totalAbertas = abertas.length;
  const paginatedAbertas = abertas.slice((openPage - 1) * pageSize, openPage * pageSize);

  // Paginação das fechadas
  const totalFechadas = fechadas.length;
  const paginatedFechadas = fechadas.slice((closedPage - 1) * pageSize, closedPage * pageSize);

  // Tempo total trabalhado (apenas fechadas)
  const totalDuracao = fechadas.reduce((acc, t) => {
    if (!t.duracao) return acc;
    const match = t.duracao.match(/(\d+)h:(\d+)min:(\d+)s/);
    if (!match) return acc;
    return acc + parseInt(match[1]) * 3600 + parseInt(match[2]) * 60 + parseInt(match[3]);
  }, 0);
  const horasTotais = Math.floor(totalDuracao / 3600);
  const minutosTotais = Math.floor((totalDuracao % 3600) / 60);

  return (
    <Layout style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px", maxWidth: 1900, margin: "0 auto", width: "100%" }}>
        <ToastContainer position="top-right" autoClose={3500} />

        {/* Header moderno */}
        <div
          style={{
            background: gradientPrimary,
            borderRadius: 24,
            padding: "32px 36px",
            marginBottom: 28,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(22,119,255,0.25)",
          }}
        >
          {/* Background decorativo */}
          <div
            style={{
              position: "absolute",
              right: -30,
              top: -30,
              width: 240,
              height: 240,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: 100,
              bottom: -50,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
            }}
          />

          <Row align="middle" justify="space-between" gutter={[24, 24]}>
            <Col>
              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: "rgba(255,255,255,0.2)",
                    backdropFilter: "blur(8px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
                >
                  <FaCalendarDay style={{ fontSize: 28, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 800, letterSpacing: -0.5 }}>
                    Tarefas Diárias
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, fontWeight: 500 }}>
                    {today}
                  </Text>
                </div>
              </div>
            </Col>
            <Col style={{ textAlign: "right" }}>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 32,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: 1,
                  marginBottom: 8,
                  textShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  letterSpacing: 2,
                }}
              >
                <LiveClock />
              </div>
              <Button
                onClick={() => setAddModalOpen(true)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  color: "#fff",
                  borderRadius: 12,
                  fontWeight: 700,
                  height: 44,
                  padding: "0 24px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 15,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                <FaPlus />
                Nova Tarefa
              </Button>
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[
            {
              icon: <FaTasks style={{ fontSize: 22, color: primaryColor }} />,
              label: "Total Hoje",
              value: tarefas.length,
              bg: "#e6f0ff",
              color: primaryColor,
            },
            {
              icon: <FaFire style={{ fontSize: 22, color: primaryColor }} />,
              label: "Em Andamento",
              value: abertas.length,
              bg: "#e6f0ff",
              color: primaryColor,
            },
            {
              icon: <FaCheckCircle style={{ fontSize: 22, color: successColor }} />,
              label: "Concluídas",
              value: fechadas.length,
              bg: "#ecfdf5",
              color: successColor,
            },
            {
              icon: <FaStopwatch style={{ fontSize: 22, color: "#0ea5e9" }} />,
              label: "Tempo Trabalhado",
              value: `${horasTotais}h ${minutosTotais}min`,
              bg: "#f0f9ff",
              color: "#0ea5e9",
              isText: true,
            },
          ].map((stat) => (
            <Col xs={12} sm={6} key={stat.label}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  border: `1px solid ${stat.bg}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  backdropFilter: "blur(4px)",
                  background: "rgba(255,255,255,0.8)",
                }}
                styles={{ body: { padding: "16px 20px" } }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: stat.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 12,
                  }}
                >
                  {stat.icon}
                </div>
                <div
                  style={{
                    fontSize: stat.isText ? 18 : 28,
                    fontWeight: 800,
                    color: stat.color,
                    lineHeight: 1,
                    marginBottom: 4,
                  }}
                >
                  {stat.value}
                </div>
                <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>
                  {stat.label}
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barra de busca + ação rápida */}
        <Row gutter={16} style={{ marginBottom: 24 }} align="middle">
          <Col flex="1">
            <Input
              size="large"
              prefix={<FaSearch style={{ color: "#9ca3af" }} />}
              placeholder="Pesquisar tarefas..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOpenPage(1);
                setClosedPage(1);
              }}
              allowClear
              style={{
                borderRadius: 14,
                border: "1px solid #e5e7eb",
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
            />
          </Col>
        </Row>

        {/* Conteúdo principal */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <Spin size="large" />
          </div>
        ) : tarefas.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "80px 0",
              background: "#f9fafb",
              borderRadius: 20,
              border: "2px dashed #d1d5db",
            }}
          >
            <FaCalendarDay style={{ fontSize: 52, color: "#cbd5e1", marginBottom: 20 }} />
            <Title level={4} style={{ color: "#64748b", fontWeight: 600 }}>
              Nenhuma tarefa hoje
            </Title>
            <Text style={{ color: "#94a3b8" }}>
              Comece adicionando sua primeira tarefa do dia!
            </Text>
            <br />
            <Button
              onClick={() => setAddModalOpen(true)}
              style={{
                marginTop: 24,
                background: gradientPrimary,
                border: "none",
                color: "#fff",
                borderRadius: 12,
                fontWeight: 700,
                height: 44,
                padding: "0 28px",
                boxShadow: "0 4px 16px rgba(22,119,255,0.3)",
              }}
            >
              <FaPlus style={{ marginRight: 8 }} />
              Adicionar Tarefa
            </Button>
          </div>
        ) : (
          <>
            {/* Seção: Em Andamento */}
            {abertas.length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 16,
                  }}
                >
                  <Badge count={totalAbertas} style={{ background: primaryColor }} />
                  <Text
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#1f2937",
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                    }}
                  >
                    Em Andamento
                  </Text>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {paginatedAbertas.map((t, i) => (
                    <div
                      key={t.id || i}
                      style={{
                        opacity: closingTarefa === t.id ? 0.4 : 1,
                        transition: "opacity 0.3s",
                        pointerEvents: closingTarefa === t.id ? "none" : "auto",
                      }}
                    >
                      <TarefaCard tarefa={t} onClose={handleCloseTarefa} />
                    </div>
                  ))}
                </div>
                {totalAbertas > pageSize && (
                  <div style={{ marginTop: 20, textAlign: "center" }}>
                    <Pagination
                      current={openPage}
                      pageSize={pageSize}
                      total={totalAbertas}
                      onChange={setOpenPage}
                      showSizeChanger={false}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Seção: Concluídas */}
            {fechadas.length > 0 && (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 16,
                  }}
                >
                  <Badge count={totalFechadas} style={{ background: successColor }} />
                  <Text
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#1f2937",
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                    }}
                  >
                    Concluídas
                  </Text>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {paginatedFechadas.map((t, i) => (
                    <TarefaCard key={t.id || i} tarefa={t} onClose={handleCloseTarefa} />
                  ))}
                </div>
                {totalFechadas > pageSize && (
                  <div style={{ marginTop: 20, textAlign: "center" }}>
                    <Pagination
                      current={closedPage}
                      pageSize={pageSize}
                      total={totalFechadas}
                      onChange={setClosedPage}
                      showSizeChanger={false}
                    />
                  </div>
                )}
              </div>
            )}

            {filtered.length === 0 && search && (
              <Empty description="Nenhuma tarefa encontrada para esta busca" style={{ padding: 40 }} />
            )}
          </>
        )}

        <AddTarefaModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={loadTarefas}
          userId={userStored.id}
        />
      </Content>
    </Layout>
  );
};

export default TarefaDiaria;